import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2025-11-17 18:53:19 +0100 (Mon, 17 Nov 2025) $
//  Author    : Olli Powroznik
//  Worker    : $Author: d.rothe $Author: d.rothe
//  Revision  : $Rev: 158292 $
//  Purpose   : frontend title components
// =============================================================================
// ---Third-party imports
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Menu, Segment, Loader, Item, Input, Header, Label, Image, Dropdown } from 'semantic-ui-react';
import clsx from 'clsx';
import KeyHandler, { KEYDOWN } from 'react-key-handler';
// ---Framework imports
import { get, size } from 'ldash';
import { Actions, Sel } from 'uix/registries';
import log from 'uix/logging';
import { TypeCaption } from 'uix/types/components';
import { AutoPortal, Link, LargeThumb, HeaderSegment, XMenu, DebugFeature } from 'uix/components';
import { useSelector, useResponsive, usePersistent, useInView, useFrontendConfig, useAncestorAxis } from 'uix/hooks';
import { useImageLocs, getPagesFlat } from 'uix/viewer/hooks';
import { Mordor } from 'uix/viewer/mordor';
// ---Local imports
import { useTitleList, useMinititleInfos } from 'uix/frontend/hooks';
import { FacetPopupMenuItem } from './facets';
// ---Globals
const MAX_TITLES = 20;
const TitleDescriptions = ({ mods })=>{
    let desc = (name, value)=>{
        if (name == 'published') {
            return /*#__PURE__*/ _jsxs("div", {
                className: name,
                children: [
                    "In: ",
                    value
                ]
            }, name);
        }
        return /*#__PURE__*/ _jsx("div", {
            className: name,
            children: value
        }, name);
    };
    let fields = [
        'subtitle',
        'author',
        'published',
        'origin'
    ];
    return fields.filter((n)=>mods[n]).map((n)=>desc(n, mods[n]));
};
const TitlesHeader = ({ otid })=>{
    let request = useSelector(Sel.request);
    let activeNode = useSelector((root)=>root.data.tree.activeNode);
    if (!otid) {
        otid = get(activeNode, 'otid');
    }
    let vltypeData = useSelector((root)=>root.data.tree.byId[otid]);
    if (vltypeData && vltypeData.datatype !== 10) {
        let { otid, odid, datatype, subtype, caption } = vltypeData;
        return /*#__PURE__*/ _jsx(TypeCaption, {
            width: "23rem",
            datatype: datatype,
            subtype: subtype,
            caption: caption
        });
    } else if (!activeNode) {
        return request.id;
    } else {
        return activeNode.title;
    }
};
const Thumbs = ({ otid, onSelectTitle, thumbZoomed, setThumbZoomed })=>{
    let [ref, onScreen] = useInView({
        triggerOnce: true
    });
    let enabled = onScreen;
    let imageLocs = useImageLocs({
        otid,
        enabled
    });
    let viewer = null;
    if (imageLocs && (imageLocs?.children.length || 0) > 0) {
        let pages = getPagesFlat(imageLocs);
        let options = {
            layout: {
                limit: 100
            },
            mouse: {}
        };
        if (thumbZoomed) {
            options.mouse.wheelModeMeta = 'zoom';
            options.mouse.dragScroll = {
                x: true,
                y: true
            };
            options.layout.limit = null;
            options.viewport = {
                virtualX: null,
                virtualY: null
            };
        }
        let onSelectionChange = ({ change, selected: nextSelected })=>{
            if (size(change.clicked) > 0) {
                let id = change.clicked[0];
                if (onSelectTitle) {
                    onSelectTitle({
                        otid,
                        imageLocs,
                        pageid: id,
                        odid: imageLocs.odid
                    });
                }
            }
        };
        // XXX: resizing (via CSS) and resetting options is broken
        // setting zoomed on viewerId for reinit is kind of lame
        // but this gives us a) the application of above options
        // and not smooth-scroll artefacts
        // it would be nice to fix this
        viewer = /*#__PURE__*/ _jsxs(Item.Content, {
            className: clsx('thumbs', {
                zoomed: thumbZoomed
            }),
            children: [
                /*#__PURE__*/ _jsx(KeyHandler, {
                    keyEventName: KEYDOWN,
                    code: 'Escape',
                    onKeyHandle: ()=>setThumbZoomed(false)
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "thumbStripe",
                    children: /*#__PURE__*/ _jsx(Mordor, {
                        options: options,
                        imageLocs: imageLocs,
                        optionSet: "thumbs",
                        viewerId: `list_${otid}_${thumbZoomed}`,
                        onRectSelect: ()=>setThumbZoomed(true),
                        onSelectionChange: onSelectionChange
                    })
                }),
                /*#__PURE__*/ _jsxs(Item.Meta, {
                    children: [
                        pages.length,
                        " Seiten"
                    ]
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("span", {
                ref: ref
            }),
            viewer
        ]
    });
};
export const Title = ({ otid, caption, count, details = null, onSelectTitle, respondMode = [], listThumbsOn, withThumbs = true, thumbWidth, link })=>{
    // fetch expensive structured imageLocs after minititleinfo details are available//
    let [thumbZoomed, setThumbZoomed] = useState(false);
    let { showFulltextInfo } = useFrontendConfig('shortTitleinfo');
    let viewer = listThumbsOn && size(details) > 0 ? /*#__PURE__*/ _jsx(Thumbs, {
        otid: otid,
        onSelectTitle: onSelectTitle,
        thumbZoomed: thumbZoomed,
        setThumbZoomed: setThumbZoomed
    }) : null;
    let titleImageClick = details ? ()=>onSelectTitle({
            odid: details.odid,
            otid,
            pageid: details.op_ot_id
        }) : null;
    let mods = get(details, 'mods', {});
    let title = get(details, 'mods.title', caption);
    let fulltext = details.textstate !== 0 && showFulltextInfo;
    let unreleased = details.released === 0;
    let onlyComputer = respondMode.includes('onlyComputer');
    let thumbsBelow = thumbZoomed || !onlyComputer;
    //~ <Link request={{odid: odid, id: otid}}>{caption}</Link>
    let linkProps = {};
    if (onSelectTitle) {
        linkProps.onClick = ()=>onSelectTitle({
                otid
            });
        linkProps['as'] = 'a';
    }
    let titleLink = /*#__PURE__*/ _jsx("a", {
        ...linkProps,
        title: caption,
        children: title
    });
    if (link) {
        titleLink = /*#__PURE__*/ _jsx(Link, {
            id: otid,
            title: caption,
            children: title
        });
    }
    // TODO:
    // ++ unreleased ClassName
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("li", {
                style: {
                    display: 'flex'
                },
                className: clsx('mdEntry', count % 2 && 'odd', unreleased && 'unreleased'),
                children: [
                    withThumbs && /*#__PURE__*/ _jsx(LargeThumb, {
                        unreleased: unreleased,
                        width: thumbWidth,
                        link: linkProps,
                        pageid: details.page?.otid,
                        onClick: onSelectTitle ? titleImageClick : null,
                        pageRatio: details.page?.height / details.page?.width
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "miniTitleInfo",
                        children: [
                            /*#__PURE__*/ _jsx("h3", {
                                style: {
                                    wordBreak: 'break-word'
                                },
                                className: "title",
                                children: titleLink
                            }),
                            /*#__PURE__*/ _jsx(TitleDescriptions, {
                                mods: mods
                            }),
                            fulltext && /*#__PURE__*/ _jsx("div", {
                                className: "fullTextInfo",
                                children: " Transkription vorhanden"
                            })
                        ]
                    }),
                    !thumbsBelow && viewer
                ]
            }, otid),
            thumbsBelow && viewer
        ]
    });
};
export const Titles = ({ titles, limit, onSelectTitle, respondMode = [], thumbWidth, link, style })=>{
    /* list of <Title>*/ let { metadataListThumbWidth: thumbWidthCfg, withThumbs } = useFrontendConfig('metadataLists');
    if (!thumbWidth) {
        thumbWidth = thumbWidthCfg;
    }
    let sliced = limit ? titles.slice(0, limit) : titles;
    // fetch minititle info data for records that have no mods property
    // else assume titles are already enriched
    let titleDetails = useMinititleInfos(sliced.filter((e)=>!e.mods).map((e)=>e.otid));
    let items = sliced.map((e, i)=>/*#__PURE__*/ _createElement(Title, {
            ...e,
            count: i,
            key: e.otid,
            details: titleDetails[e.otid] || e,
            onSelectTitle: onSelectTitle,
            respondMode: respondMode,
            thumbWidth: thumbWidth,
            withThumbs: withThumbs,
            link: link
        }));
    let styles = {
        margin: 0,
        padding: 0,
        ...style
    };
    return /*#__PURE__*/ _jsx("ul", {
        style: styles,
        id: "allDocuments",
        className: clsx('mdList', ...respondMode),
        children: items
    });
};
export const MiniTitleInfo = ({ otid, onSelectTitle, basic, listThumbsOn, thumbWidth, className, link, style, ...props })=>{
    let details = useMinititleInfos(otid);
    let [respondMode, ref] = useResponsive();
    if (details) {
        // data loaded / available
        let title = /*#__PURE__*/ _jsx(Title, {
            otid: otid,
            details: details,
            respondMode: respondMode,
            onSelectTitle: onSelectTitle ? (title)=>onSelectTitle(title) : undefined,
            thumbWidth: thumbWidth,
            listThumbsOn: listThumbsOn,
            link: link
        });
        if (basic) {
            return title;
        }
        return /*#__PURE__*/ _jsx("div", {
            className: className,
            ref: ref,
            children: /*#__PURE__*/ _jsx(Segment, {
                className: clsx('title', ...respondMode),
                ...props,
                children: /*#__PURE__*/ _jsx(Item.Group, {
                    divided: true,
                    children: title
                })
            })
        });
    }
    return null;
};
export const PageTitleInfo = ({ node, ...props })=>{
    // XXX: needs to check root types on dconfig
    let first_ancestor_md = node.ancestorsAndSelf.filter((e)=>e.datatype == 1)[0];
    // for slimlinked pages the default ancestor axis is the slimlinked path (as for normal links)
    let enabled_fallback = !first_ancestor_md && [
        0,
        3
    ].includes(node.datatype) && node.slimlink;
    let fallback_aaxis_node = useAncestorAxis({
        otid: node.otid,
        enabled: enabled_fallback
    });
    if (!first_ancestor_md) {
        if (enabled_fallback && fallback_aaxis_node) {
            first_ancestor_md = fallback_aaxis_node.ancestorsAndSelf.filter((e)=>e.datatype == 1)[0];
            if (!first_ancestor_md) {
                return null;
            }
        } else {
            return null;
        }
    }
    let { otlink, otid } = first_ancestor_md;
    return /*#__PURE__*/ _jsx(MiniTitleInfo, {
        ...props,
        otid: otlink || otid
    });
};
export const TitleList = ({ onSelectTitle })=>{
    /* <Titles> with data via useTitleList()
   */ let [source, setDataSource] = usePersistent('datasource', 'db');
    let [searchTerm, setSearchTerm] = usePersistent('searchTerm', '');
    let [respondMode, ref] = useResponsive();
    let [subdomains, setSubdomains] = usePersistent('subdomains', true);
    let limit = MAX_TITLES;
    let { list_otid, titles, facets, total, loading, LoadMoreIfRendered } = useTitleList({
        subdomains,
        limit,
        searchTerm,
        source
    });
    if (!titles) {
        return /*#__PURE__*/ _jsx(Loader, {
            active: true
        });
    }
    let menu = /*#__PURE__*/ _jsxs(XMenu, {
        children: [
            /*#__PURE__*/ _jsx(XMenu.Item, {
                active: subdomains,
                icon: "sitemap",
                onClick: ()=>setSubdomains(!subdomains)
            }),
            /*#__PURE__*/ _jsx(XMenu.Input, {
                debounce: true,
                value: searchTerm,
                iconPosition: "left",
                placeholder: "search",
                icon: 'search',
                onValueChange: (value)=>setSearchTerm(value)
            }),
            /*#__PURE__*/ _jsxs(DebugFeature, {
                children: [
                    /*#__PURE__*/ _jsx(XMenu.Dropdown, {
                        options: [
                            'db',
                            'index-desc'
                        ].map((v)=>({
                                value: v,
                                key: v,
                                text: v
                            })),
                        value: source,
                        onChange: (e, data)=>setDataSource(data.value)
                    }),
                    /*#__PURE__*/ _jsx(XMenu.PPrintButton, {
                        data: {
                            limit,
                            total,
                            loading
                        },
                        toggle: true
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(FacetPopupMenuItem, {
                facets: facets
            })
        ]
    });
    let header = /*#__PURE__*/ _jsx(TitlesHeader, {
        otid: list_otid
    });
    let subheader = `${titles?.length} / ${total}`;
    return /*#__PURE__*/ _jsx(HeaderSegment, {
        major: true,
        flex: true,
        icon: "list",
        header: header,
        subheader: subheader,
        menu: menu,
        loading: loading,
        children: /*#__PURE__*/ _jsxs("div", {
            ref: ref,
            style: {
                flex: 1,
                overflow: 'auto'
            },
            children: [
                /*#__PURE__*/ _jsx(Titles, {
                    titles: titles,
                    onSelectTitle: onSelectTitle,
                    respondMode: respondMode
                }),
                LoadMoreIfRendered
            ]
        })
    });
};
